import React, { useEffect, useMemo, useRef } from 'react';

import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import type { ChartPreviewRequest } from './chartPreviewUtils';
import {
  applyColumnCornerRadius,
  applyPreviewTooltipStyle,
  buildXYTooltipHTML,
  getPreviewColors,
} from './chartPreviewStyles';
import useAmChartsResize from './useAmChartsResize';

type Props = {
  chart: ChartPreviewRequest;
  height?: number | string;
  width?: number | string;
};

const SidewaysBarChartPreview = ({ chart, height, width }: Props): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const theme = useTheme();
  useAmChartsResize(ref, chartRef);
  const colors = useMemo(() => getPreviewColors(theme), [theme]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const { categoryField, valueFields, data } = chart;
    if (!categoryField || !valueFields?.length || !data?.length) {
      return;
    }

    const valueField = valueFields[0];
    const values = data
      .map((row) => (typeof row[valueField] === 'number' ? row[valueField] : null))
      .filter((value): value is number => typeof value === 'number');
    const lowestValue = values.reduce((min, value) => (value < min ? value : min), 0);
    const highestValue = values.reduce((max, value) => (value > max ? value : max), 0);

    const chartInstance = am4core.create(container, am4charts.XYChart);
    chartRef.current = chartInstance;
    chartInstance.data = data;
    chartInstance.colors.list = colors;
    chartInstance.numberFormatter.numberFormat = '$#,###.00';

    const categoryAxis = chartInstance.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = categoryField;
    categoryAxis.renderer.minGridDistance = 1;
    categoryAxis.renderer.cellStartLocation = 0;
    categoryAxis.renderer.cellEndLocation = 1;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.inversed = true;
    categoryAxis.renderer.line.strokeOpacity = 0;
    categoryAxis.renderer.ticks.template.disabled = true;
    const categoryLabel = categoryAxis.renderer.labels.template;
    categoryLabel.fill = am4core.color(theme.palette.text.secondary);
    categoryLabel.fontSize = 14;
    categoryLabel.truncate = true;
    categoryLabel.maxWidth = 200;
    categoryLabel.marginLeft = -15;
    categoryLabel.paddingLeft = 0;
    categoryLabel.adapter.add('text', (_text) => '[text-transform: capitalize]{category}');
    categoryLabel.interactionsEnabled = true;
    categoryLabel.tooltipText = '{category}';
    categoryLabel.adapter.add('tooltipText', (text, target) => {
      if (!target.isOversized) {
        return undefined;
      }
      const axisDataItem = target.dataItem;
      if (axisDataItem instanceof am4charts.CategoryAxisDataItem) {
        return axisDataItem.category;
      }
      return text;
    });

    const valueAxis = chartInstance.xAxes.push(new am4charts.ValueAxis());
    valueAxis.min = lowestValue;
    valueAxis.max = highestValue;
    valueAxis.renderer.labels.template.disabled = true;
    valueAxis.renderer.line.strokeOpacity = 0;
    valueAxis.renderer.grid.template.disabled = true;
    valueAxis.cursorTooltipEnabled = false;
    valueAxis.padding(0, 60, 0, 0);

    const series = chartInstance.series.push(new am4charts.ColumnSeries());
    series.dataFields.categoryY = categoryField;
    series.dataFields.valueX = valueField;
    series.name = valueField;
    series.sequencedInterpolation = true;
    series.columns.template.strokeWidth = 0;
    series.columns.template.height = am4core.percent(85);
    series.columns.template.maxHeight = 30;

    applyColumnCornerRadius(series.columns.template.column, {
      direction: 'horizontal',
      valueKey: 'valueX',
      radius: 4,
    });

    const columnLabels = series.bullets.push(new am4charts.LabelBullet());
    columnLabels.label.text = "[font-size: 12px]{valueX.formatNumber('$#,###.00')}";
    columnLabels.dx = 8;
    columnLabels.label.fill = am4core.color(theme.palette.text.secondary);
    columnLabels.label.horizontalCenter = 'left';
    columnLabels.label.truncate = false;
    columnLabels.label.hideOversized = false;
    columnLabels.adapter.add('locationX', (locationX, target) => {
      if (target.dataItem?.values?.valueX?.value && target.dataItem.values.valueX.value < 0) {
        return 1;
      }
      return 0;
    });

    series.columns.template.adapter.add('fill', (fill, target) => {
      const index = target.dataItem?.index ?? 0;
      return colors[index % colors.length] ?? fill;
    });
    series.columns.template.adapter.add('stroke', (stroke, target) => {
      const index = target.dataItem?.index ?? 0;
      return colors[index % colors.length] ?? stroke;
    });

    series.columns.template.tooltipHTML = buildXYTooltipHTML(theme, {
      categoryKey: 'categoryY',
      valueKey: 'valueX',
    });
    series.columns.template.tooltip = new am4core.Tooltip();
    applyPreviewTooltipStyle(series.columns.template.tooltip, theme, {
      background: 'default',
      opacity: 0.9,
      strokeWidth: 0.2,
      textColor: theme.palette.text.primary,
    });

    const cursor = new am4charts.XYCursor();
    cursor.lineX.disabled = true;
    cursor.lineY.disabled = true;
    cursor.behavior = 'none';
    chartInstance.cursor = cursor;

    return () => {
      chartInstance.dispose();
      chartRef.current = null;
    };
  }, [chart, colors, theme]);

  return <Box ref={ref} sx={{ height: height ?? 240, width: width ?? '100%' }} />;
};

export default SidewaysBarChartPreview;
